import { Avatar, Flex, Loader, ScrollAreaAutosize, Text } from "@mantine/core";
import { BoxTitle, Container } from "./StyledComponents";
import {
  ArrowLeftEndOnRectangleIcon,
  ArrowRightEndOnRectangleIcon,
  MapPinIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import styled, { keyframes } from "styled-components";
import { theme } from "../../theme";
import { useEffect, useState } from "react";
import { getLiveActivity } from "../../api/dashboardApi";
import dayjs from "dayjs";
import CustomSelect from "../common/Inputs/CustomSelect";
import CustomSearchInput from "../common/Inputs/CustomSearchInput";
import { useFilterContext } from "../../context/DashboardFilterContext";
import { useAuth } from "../../context/AuthContext";



function getAvatarGrad(name: string) {
  const charCode = name?.charCodeAt(0) || 0;
  return AVATAR_GRADS[charCode % AVATAR_GRADS.length];
}

function getInitials(name: string) {
  return (name || "?")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}


const LiveActivity = ({ flex }: { flex: any }) => {
  const { terminalSelectData } = useAuth();
  const { filters } = useFilterContext();
  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [otherFilters, setOtherFilters] = useState({
    name: "",
    terminal: "",
    action: "",
    status: "",
  });

  const getData = async () => {
    setIsLoading(true);
    try {
      const response: any = await getLiveActivity(filters, otherFilters);
      if (response?.success) {
        setActivities(response?.data?.activities || []);
      }
    } catch (error) {}
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, [filters, otherFilters]);

  return (
    <Container $flex={flex}>
      <Flex justify="space-between" align="center" mb={16}>
        <Flex align="center" gap={10}>
          <HeaderIconWrap>
            <BoltIcon width={18} strokeWidth={2} />
          </HeaderIconWrap>
          <BoxTitle style={{ margin: 0 }}>Canlı Aktivite</BoxTitle>
        </Flex>
        <LiveIndicator>
          <PulseDot />
          <Text size="xs" fw={700} color="#059669" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Canlı
          </Text>
        </LiveIndicator>
      </Flex>

      <FilterBar>
        <CustomSelect
          data={[
            { value: "1", label: "Giriş" },
            { value: "0", label: "Çıkış" },
          ]}
          onChange={(e: any) => setOtherFilters((prev) => ({ ...prev, action: e }))}
          placeholder="Yön"
          w={110}
          searchable={false}
          clearable
        />
        <CustomSelect
          data={terminalSelectData}
          onChange={(e: any) => setOtherFilters((prev) => ({ ...prev, terminal: e }))}
          placeholder="Terminal"
          flex={1}
          clearable
        />
        <CustomSelect
          data={[
            { value: "1", label: "Vaktinde" },
            { value: "2", label: "Erken" },
            { value: "3", label: "Geç" },
          ]}
          onChange={(e: any) => setOtherFilters((prev) => ({ ...prev, status: e }))}
          placeholder="Durum"
          w={120}
          searchable={false}
          clearable
        />
        <CustomSearchInput
          onChange={(e: any) => setOtherFilters((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Personel ara..."
          w={180}
        />
      </FilterBar>

      {isLoading ? (
        <Flex h="100%" justify="center" align="center">
          <Loader size="sm" color="blue.6" />
        </Flex>
      ) : (
        <ScrollAreaAutosize h="78%" scrollbarSize={6}>
          <Flex direction="column" gap={8} pr={10}>
            {activities.map((activity: any, i: number) => {
              const isEntry = activity.action === 1;
              const actColor = isEntry ? ACTION_COLORS.entry : ACTION_COLORS.exit;
              const statusStyle = STATUS_COLORS[activity?.status?.status] || STATUS_COLORS[3];
              const [gradFrom, gradTo] = getAvatarGrad(activity.employeeName);

              return (
                <ActivityRow key={i} $delay={i * 50}>
                  <DirectionIconWrap $bg={actColor.iconBg}>
                    {isEntry ? (
                      <ArrowRightEndOnRectangleIcon width={20} strokeWidth={2} color={actColor.color} />
                    ) : (
                      <ArrowLeftEndOnRectangleIcon width={20} strokeWidth={2} color={actColor.color} />
                    )}
                  </DirectionIconWrap>

                  <ActivityCard>
                    <Flex align="center" gap={12} flex={1} style={{ minWidth: 0 }}>
                      {/* HATASIZ AVATAR KULLANIMI */}
                      <StyledAvatarWrapper $from={gradFrom} $to={gradTo}>
                         <Avatar radius="xl" size="md">
                           {getInitials(activity.employeeName)}
                         </Avatar>
                      </StyledAvatarWrapper>
                      
                      <Flex direction="column" gap={2} style={{ minWidth: 0, flex: 1 }}>
                        <Text fw={600} size="sm" color="#111827" truncate>
                          {activity.employeeName}
                        </Text>
                        <Flex align="center" gap={4}>
                          <MapPinIcon height={12} color="#94A3B8" />
                          <Text size="xs" color="#64748B" truncate fw={500}>
                            {activity.terminalName}
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>

                    <Flex align="center" gap={12} style={{ flexShrink: 0 }}>
                      <StatusBadge $bg={statusStyle.bg} $color={statusStyle.color}>
                        {activity?.status?.message}
                      </StatusBadge>
                      <Text size="sm" fw={600} color="#475569" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {dayjs(activity.date).format("HH:mm")}
                      </Text>
                    </Flex>
                  </ActivityCard>
                </ActivityRow>
              );
            })}
          </Flex>
        </ScrollAreaAutosize>
      )}
    </Container>
  );
};

export default LiveActivity;


const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulseLive = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
  60% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
`;

const ACTION_COLORS = {
  entry: { color: "#10B981", iconBg: "#ECFDF5" },
  exit: { color: "#EF4444", iconBg: "#FEF2F2" },
};

const STATUS_COLORS = [
  { color: "#059669", bg: "#ECFDF5" },
  { color: "#D97706", bg: "#FFFBEB" },
  { color: "#DC2626", bg: "#FEF2F2" },
  { color: "#2563EB", bg: "#EFF6FF" },
];

const AVATAR_GRADS = [
  ["#a1887f", "#8d6e63"],
  ["#90a4ae", "#78909c"],
  ["#81c784", "#66bb6a"],
  ["#e57373", "#ef5350"],
  ["#64b5f6", "#42a5f5"],
];

const HeaderIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${theme.colors.brand.normal}10;
  color: ${theme.colors.brand.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.colors.brand.normal}20;
`;

const LiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #ecfdf5;
  border-radius: 50px;
  border: 1px solid #a7f3d0;
`;

const PulseDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #10b981;
  animation: ${pulseLive} 2s infinite;
`;

const FilterBar = styled(Flex)`
  gap: 10px;
  margin-bottom: 16px;
  background-color: #f8fafc;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
`;

const ActivityRow = styled.div<{ $delay: number }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px;
  border-radius: 14px;
  animation: ${slideIn} 0.4s ease both;
  animation-delay: ${(p) => p.$delay}ms;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;

  &:hover {
    transform: translateY(-2px) scale(1.015);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
    z-index: 2;
  }
`;

const DirectionIconWrap = styled.div<{ $bg: string }>`
  display: flex;
  width: 44px;
  height: 44px;
  justify-content: center;
  align-items: center;
  background-color: ${({ $bg }) => $bg};
  border-radius: 10px;
  flex-shrink: 0;
`;

const ActivityCard = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  min-width: 0;
  transition: border-color 0.2s;

  ${ActivityRow}:hover & {
    border-color: ${theme.colors.brand.normal}30;
  }
`;

const StyledAvatarWrapper = styled.div<{ $from: string; $to: string }>`
  .mantine-Avatar-placeholder {
    background: linear-gradient(135deg, ${({ $from }) => $from}, ${({ $to }) => $to}) !important;
    color: white !important;
    font-weight: 700;
  }
`;

const StatusBadge = styled.div<{ $bg: string; $color: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 80px;
  height: 26px;
  padding: 0 10px;
  border-radius: 50px;
  color: ${({ $color }) => $color};
  background-color: ${({ $bg }) => $bg};
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
`;